import { useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useGenerateVariantMatrix } from '../../hooks';
import type { VariantMatrixGenerateResult } from '../../model/types';
import type { AxisAttribute } from './CreateVariantsDialog';

export interface GenerateVariantMatrixDialogProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  axisAttributes: AxisAttribute[];
  onGenerated: (result: VariantMatrixGenerateResult) => void;
}

/**
 * One-action variant matrix wizard (epic-catalog-007, P8; server-side generation — combination math,
 * SKU templates run in web-catalog-master-service, existing combinations are skipped idempotently). The
 * manager enters comma-separated values per axis (option CODES for SELECT axes).
 */
export function GenerateVariantMatrixDialog({
  open,
  onClose,
  productId,
  axisAttributes,
  onGenerated,
}: GenerateVariantMatrixDialogProps) {
  const { t } = useTranslation('catalog');
  const generate = useGenerateVariantMatrix();

  const [valuesByAxis, setValuesByAxis] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const parsedAxes = useMemo(
    () =>
      axisAttributes.map((axis) => ({
        attributeCode: axis.attributeCode,
        values: (valuesByAxis[axis.attributeCode] ?? '')
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0),
      })),
    [axisAttributes, valuesByAxis],
  );

  const combinationCount = useMemo(
    () => parsedAxes.reduce((acc, axis) => acc * (axis.values.length || 0), parsedAxes.length ? 1 : 0),
    [parsedAxes],
  );

  const valid = parsedAxes.length > 0 && parsedAxes.every((axis) => axis.values.length > 0);

  const submit = () => {
    setError(null);
    generate.mutate(
      {
        productId,
        body: {
          axes: parsedAxes,
        },
      },
      {
        onSuccess: (result) => {
          onGenerated(result);
          onClose();
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('generateMatrixDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('generateMatrixDialog.subtitle')}
          </Typography>
          {axisAttributes.map((axis) => (
            <TextField
              key={axis.attributeCode}
              size="small"
              label={t('generateMatrixDialog.axisValuesLabel', { axis: axis.attributeCode })}
              placeholder={t('generateMatrixDialog.axisValuesPlaceholder')}
              value={valuesByAxis[axis.attributeCode] ?? ''}
              onChange={(e) =>
                setValuesByAxis((prev) => ({ ...prev, [axis.attributeCode]: e.target.value }))
              }
              fullWidth
            />
          ))}

          {valid ? (
            <Alert severity="info" variant="outlined">
              {t('generateMatrixDialog.combinationCount', { count: combinationCount })}
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || generate.isPending}>
          {t('generateMatrixDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
